// ============================================================
//  src/api/client.js — Phase 4b: import/export added
//  Phase 4c: command publish/unpublish/test added
//  Phase 7: automations added
//  Phase 8: PocketFi merchant & products added
// ============================================================
import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
});

// ── Auth token injection ──────────────────────────────────────
API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('sAuthToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ── 401 → logout ─────────────────────────────────────────────
API.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('sAuthToken');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const loginUser     = data => API.post('/auth/login/',    data);
export const registerUser  = data => API.post('/auth/register/', data);
export const verifyEmail   = data => API.post('/auth/verify/',   data);
export const resendVerification = data => API.post('/auth/verify/', { ...data, action: 'resend' });
export const forgotPassword = data => API.post('/auth/forgot/',   data);
export const forgotPasswordReq    = data => API.post('/auth/forgot/', { ...data, step: 'request' });
export const forgotPasswordVerify = data => API.post('/auth/forgot/', { ...data, step: 'verify'  });
export const forgotPasswordReset  = data => API.post('/auth/forgot/', { ...data, step: 'reset'   });
export const logoutUser    = ()   => API.post('/auth/logout/');
export const getDashboard  = ()   => API.get('/dashboard/');

// ── Bots ──────────────────────────────────────────────────────
export const listBots  = ()         => API.get('/bots/');
export const getBot    = bId        => API.get('/bots/detail/', { params: { bId } });
export const createBot = data       => API.post('/bots/', { action: 'create',  ...data });
export const updateBot = data       => API.post('/bots/', { action: 'update',  ...data });
export const toggleBot = (bId, active) => API.post('/bots/', { action: 'toggle', bId, active });
export const deleteBot = bId        => API.post('/bots/', { action: 'delete',  bId });
export const cloneBot  = bId        => API.post('/bots/', { action: 'clone',   bId });
export const getBotHealth = (bId, refresh = false) =>
  API.get('/bots/health/', { params: { bId, ...(refresh ? { refresh: 1 } : {}) } });
export const getBotPhoneNumbers = bId => API.get('/bots/phone-numbers/', { params: { bId } });

// ── WhatsApp admin (manage the bot from the owner's phone) ────
export const getWhatsAppAdmin    = bId => API.get('/bots/whatsapp-admin/', { params: { bId } });
export const whatsAppAdminAction = (bId, action, data = {}) => API.post('/bots/whatsapp-admin/', { bId, action, ...data });
export const getBotProfile    = bId       => API.get('/bots/profile/', { params: { bId } });
export const updateBotProfile = (bId, data) => API.post('/bots/profile/', { action: 'update', bId, ...data });
export const uploadBotProfilePhoto = (bId, file) => {
  const fd = new FormData();
  fd.append('action', 'upload_photo');
  fd.append('bId', bId);
  fd.append('photo', file);
  return API.post('/bots/profile/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// ── Bot Import (multipart upload) ────────────────────────────
//   opts.password   — required for password-protected (.ckbot) packages
//   opts.sections   — array of section keys to bring in (default: all in the package)
//   opts.onConflict — 'skip' keeps items the bot already has, 'replace' overwrites them
//   opts.goLive     — publish new commands immediately (only honoured for your own package)
const importForm = (file, bId, opts) => {
  const form = new FormData();
  form.append('bot_file', file);
  if (bId) form.append('bId', bId);
  if (opts.password) form.append('password', opts.password);
  if (opts.sections) form.append('sections', opts.sections.join(','));
  if (opts.onConflict) form.append('onConflict', opts.onConflict);
  if (opts.goLive) form.append('goLive', '1');
  if (opts.preview) form.append('action', 'preview');
  return form;
};
export const importBot = (file, bId = '', opts = {}) =>
  API.post('/bots/import/', importForm(file, bId, opts), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Opens a package without changing anything: sections, what it would replace,
// and a security review. Rejects with 401 { code: 'password_required' | 'wrong_password' }.
export const previewImport = (file, bId = '', password = '') =>
  API.post('/bots/import/', importForm(file, bId, { password, preview: true }), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// ── Bot Export ───────────────────────────────────────────────
// Item counts per section, for the export dialog.
export const getExportSummary = bId =>
  API.get('/bots/export/', { params: { bId, action: 'summary' } });

// Downloads a password-protected .ckbot package. POST so the password never
// appears in a URL (and so in server or proxy logs).
//   opts.password       — required
//   opts.sections       — array of section keys (commands + panel are always included)
//   opts.includeSecrets — keep secret panel values in the package
//   opts.expires        — 'never' | '1d' | '7d' | '30d'
//   opts.scope          — 'anyone' (with the password) | 'owner' (only this account)
export const exportBot = (bId, opts = {}) => {
  const form = new FormData();
  form.append('bId', bId);
  form.append('password', opts.password ?? '');
  if (opts.sections) form.append('sections', opts.sections.join(','));
  if (opts.includeSecrets) form.append('includeSecrets', '1');
  form.append('expires', opts.expires ?? 'never');
  form.append('scope', opts.scope ?? 'anyone');
  return API.post('/bots/export/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  });
};

// ── Commands ──────────────────────────────────────────────────
export const listCommands  = bId        => API.get('/bots/commands/',  { params: { bId } });
export const getCommand    = (bId, cId) => API.get('/bots/commands/',  { params: { bId, cId } });
export const createCommand = data       => API.post('/bots/commands/', { action: 'create', ...data });
export const updateCommand = data       => API.post('/bots/commands/', { action: 'update', ...data });
export const deleteCommand = (bId, cId) => API.post('/bots/commands/', { action: 'delete', bId, cId });

// ── Commands: publish / unpublish / test ──────────────────────
export const publishCommand   = (bId, cId) =>
  API.post('/bots/commands/', { action: 'publish',   bId, cId });

export const unpublishCommand = (bId, cId) =>
  API.post('/bots/commands/', { action: 'unpublish', bId, cId });

export const testCommand = ({ bId, cId, testMessage }) =>
  API.post('/bots/commands/', { action: 'test', bId, cId, testMessage });

export const toggleCommandActive = (bId, cId, active) =>
  API.post('/bots/commands/', { action: 'toggle_active', bId, cId, active: active ? 1 : 0 });

// ── Commands: unpublished changes + version history ──────────
// Editing a live command saves "unpublished changes"; customers keep the
// live version until publishCommand(). Every publish is kept as a version.
// "Try a message": which command would answer this text (triggers → phrases → catch-all).
// draft = the command being edited { cId, cTrigger, cTriggerType, cPhrases }, ai = also ask the AI.
export const matchTestCommand = (bId, text, draft, ai = false) =>
  API.post('/bots/commands/', { action: 'match_test', bId, text, draft, ai });
export const discardCommandDraft = (bId, cId) =>
  API.post('/bots/commands/', { action: 'discard_draft', bId, cId });
export const listCommandVersions = (bId, cId) =>
  API.post('/bots/commands/', { action: 'versions', bId, cId });
//   publish=false → restored as unpublished changes (test first); true → live now
export const restoreCommandVersion = (bId, cId, vId, publish = false) =>
  API.post('/bots/commands/', { action: 'restore_version', bId, cId, vId, publish: publish ? 1 : 0 });

// ── Restore points (whole-bot snapshots, kept on the server) ──
export const getRestorePoints     = bId => API.get('/bots/restore-points/', { params: { bId } });
export const createRestorePoint   = (bId, label) => API.post('/bots/restore-points/', { action: 'create', bId, label });
export const previewRestorePoint  = (bId, rpId) => API.post('/bots/restore-points/', { action: 'preview', bId, rpId });
export const restoreRestorePoint  = (bId, rpId, sections) => API.post('/bots/restore-points/', { action: 'restore', bId, rpId, ...(sections ? { sections } : {}) });
export const deleteRestorePoint   = (bId, rpId) => API.post('/bots/restore-points/', { action: 'delete', bId, rpId });

// ── Transfer ownership (owner only, permanent) ──
export const getTransferSummary      = bId => API.get('/bots/transfer/', { params: { bId } });
export const lookupTransferRecipient = (bId, email) => API.post('/bots/transfer/', { bId, action: 'lookup', email });
export const sendTransferCode        = (bId, email, confirmName, wipeSecrets) => API.post('/bots/transfer/', { bId, action: 'send_code', email, confirmName, wipeSecrets: !!wipeSecrets });
export const transferBot             = (bId, tId, code, password, confirmName) => API.post('/bots/transfer/', { bId, action: 'transfer', tId, code, password, confirmName });
export const cancelTransfer          = bId => API.post('/bots/transfer/', { bId, action: 'cancel' });

// ── Automations ────────────────────────────────────────────────
export const listAutomations      = (bId)                    => API.get('/bots/automations/', { params: { bId } });
export const getAutomation        = (bId, aId)               => API.get('/bots/automations/', { params: { bId, aId } });
export const createAutomation     = (data)                   => API.post('/bots/automations/', { action: 'create',      ...data });
export const saveAutomation       = (data)                   => API.post('/bots/automations/', { action: 'save',        ...data });
export const activateAutomation   = (bId, aId)               => API.post('/bots/automations/', { action: 'activate',    bId, aId });
export const deactivateAutomation = (bId, aId)               => API.post('/bots/automations/', { action: 'deactivate',  bId, aId });
export const duplicateAutomation  = (bId, aId)               => API.post('/bots/automations/', { action: 'duplicate',   bId, aId });
export const deleteAutomation     = (bId, aId)               => API.post('/bots/automations/', { action: 'delete',      bId, aId });

// ── Panel ─────────────────────────────────────────────────────
export const getPanel       = bId        => API.get('/bots/panel/',  { params: { bId } });
export const savePanelField = data       => API.post('/bots/panel/', { action: 'save',         ...data });
export const updatePanelValue= data      => API.post('/bots/panel/', { action: 'update_value', ...data });
export const deletePanelField= (bId,pId) => API.post('/bots/panel/', { action: 'delete', bId, pId });

// ── Bot Users ─────────────────────────────────────────────────
export const listBotUsers     = (bId, limit=100, offset=0) =>
  API.get('/bots/users/', { params: { bId, limit, offset } });

export const exportBotUsers   = (bId) =>
  API.get('/bots/users/', { params: { bId, action: 'export' }, responseType: 'blob' });

export const blockBotUser     = (bId, uId) =>
  API.post('/bots/users/', { action: 'block',   bId, uId });

export const unblockBotUser   = (bId, uId) =>
  API.post('/bots/users/', { action: 'unblock', bId, uId });

export const sendBotUserMsg   = (bId, uId, message, buttons = []) =>
  API.post('/bots/users/', { action: 'send',      bId, uId, message, buttons });

export const broadcastBotMsg  = (bId, message, buttons = []) =>
  API.post('/bots/users/', { action: 'broadcast', bId, message, buttons });

// ── Logs ──────────────────────────────────────────────────────
export const listLogs  = (bId, level='', limit=100) =>
  API.get('/bots/logs/',  { params: { bId, level, limit } });
export const clearLogs = bId => API.post('/bots/logs/', { action: 'clear', bId });

// ── Collaborations ────────────────────────────────────────────
export const collabApi = (data) =>
  API.post('/collaborations/', data);

export const listCollabs         = ()             => collabApi({ action: 'list' });
export const listReceivedInvites = ()             => collabApi({ action: 'list_received' });
export const inviteCollaborator  = (data)         => collabApi({ action: 'invite',  ...data });
export const respondToInvite     = (cId, accept)  => collabApi({ action: 'respond', cId, accept });
export const removeCollaborator  = (cId)          => collabApi({ action: 'remove',  cId });
export const updateCollabAccess  = (cId, perms)   => collabApi({ action: 'update',  cId, permissions: perms });
export const listCollabBots      = ()             => collabApi({ action: 'list_bots' });

// ── Community ─────────────────────────────────────────────────
export const listCommunityPosts = (limit = 20, offset = 0, filter = 'latest') =>
  API.get('/community/', { params: { action: 'list', limit, offset, filter } });

export const getCommunityPost = (pId) =>
  API.get('/community/', { params: { action: 'get', pId } });

export const createCommunityPost = (formData) =>
  API.post('/community/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

export const deleteCommunityPost = (pId) =>
  API.post('/community/', { action: 'delete_post', pId });

export const resolveCommunityPost = (pId) =>
  API.post('/community/', { action: 'resolve', pId });

export const likeCommunityPost = (pId) =>
  API.post('/community/', { action: 'like', pId });

export const createCommunityReply = (pId, body) =>
  API.post('/community/', { action: 'reply', pId, body });

export const acceptCommunityReply = (rId, pId) =>
  API.post('/community/', { action: 'accept_reply', rId, pId });

export const deleteCommunityReply = (rId) =>
  API.post('/community/', { action: 'delete_reply', rId });

// ── Scheduled Messages ────────────────────────────────────────
export const listScheduled    = (bId) =>
  API.get('/bots/scheduled/', { params: { bId } });

export const createScheduled  = (data) =>
  API.post('/bots/scheduled/', { action: 'create', ...data });

export const cancelScheduled  = (bId, smId) =>
  API.post('/bots/scheduled/', { action: 'cancel', bId, smId });

export const deleteScheduled  = (bId, smId) =>
  API.post('/bots/scheduled/', { action: 'delete', bId, smId });

// ── Billing ──────────────────────────────────────────────────
export const getBillingOverview = () => API.get('/billing/');
export const getBillingHistory  = () => API.get('/billing/', { params: { view: 'history' } });

export const upgradePlan = (planSlug) =>
  API.post('/billing/', { action: 'upgrade', plan_slug: planSlug });

export const confirmPayment = (paymentId) =>
  API.post('/billing/', { action: 'confirm', payment_id: paymentId });

// ── Admin: Plans ─────────────────────────────────────────────
export const adminListPlans  = () => API.get('/admin/plans/');
export const adminCreatePlan = (data) => API.post('/admin/plans/', { action: 'create', ...data });
export const adminUpdatePlan = (data) => API.post('/admin/plans/', { action: 'update', ...data });
export const adminDeletePlan = (plId) => API.post('/admin/plans/', { action: 'delete', plId });
export const adminSavePocketfiConfig = (data) =>
  API.post('/admin/plans/', { action: 'save_pocketfi_config', ...data });

// ── Bot Users (analytics) ─────────────────────────────────────
export const listBotUsersAll  = (bId, limit = 2000, offset = 0) =>
  API.get('/bots/users/', { params: { bId, limit, offset } });

// Alias used by AnalyticsTab
export const getBotUsers      = (bId, page = 1, limit = 2000) =>
  API.get('/bots/users/', { params: { bId, limit, offset: (page - 1) * limit } });

// ── AI Integrations ───────────────────────────────────────────
export const listAiIntegrations  = ()                        => API.get('/ai/integrations/');
export const saveAiIntegration   = (provider, apiKey, model) => API.post('/ai/integrations/', { provider, apiKey, model });
export const deleteAiIntegration = (provider)                => API.delete('/ai/integrations/', { data: { provider } });

// ── Activity Feed ─────────────────────────────────────────────
export const getBotActivity     = (bId, limit = 100, offset = 0) =>
  API.get('/bots/activity/', { params: { bId, limit, offset } });

export const getBotUserActivity = (bId, chatId, limit = 200) =>
  API.get('/bots/activity/', { params: { bId, chatId, limit } });

// ── Persistent Menu ───────────────────────────────────────────
export const getMenu     = (bId)         => API.get('/bots/menu/',  { params: { bId } });
export const saveMenu    = (bId, items)  => API.post('/bots/menu/', { action: 'save',    bId, menu_items: items });
export const publishMenu = (bId, items)  => API.post('/bots/menu/', { action: 'publish', bId, menu_items: items });
export const clearMenu   = (bId)         => API.post('/bots/menu/', { action: 'delete',  bId });

// ── Custom Endpoints ─────────────────────────────────────────
export const listEndpoints          = (bId)                  => API.get('/bots/endpoints/', { params: { bId } });
export const getEndpoint            = (bId, eId)              => API.post('/bots/endpoints/', { action: 'get', bId, eId });
export const createEndpoint         = (bId, data)             => API.post('/bots/endpoints/', { action: 'create', bId, ...data });
export const updateEndpoint         = (bId, eId, data)        => API.post('/bots/endpoints/', { action: 'update', bId, eId, ...data });
export const toggleEndpoint         = (bId, eId, active)      => API.post('/bots/endpoints/', { action: 'toggle', bId, eId, active });
export const regenerateEndpointSlug = (bId, eId)              => API.post('/bots/endpoints/', { action: 'regenerate_slug', bId, eId });
export const deleteEndpoint         = (bId, eId)              => API.post('/bots/endpoints/', { action: 'delete', bId, eId });
export const listEndpointLogs       = (bId, eId, limit = 100) => API.post('/bots/endpoints/', { action: 'logs', bId, eId, limit });
export const clearEndpointLogs      = (bId, eId)              => API.post('/bots/endpoints/', { action: 'clear_logs', bId, eId });

// ── Custom Endpoints: raw, cPanel-style error_log file ────────
export const getEndpointErrorLog   = (bId, eId) =>
  API.post('/bots/endpoints/', { action: 'error_log', bId, eId });

export const clearEndpointErrorLog = (bId, eId) =>
  API.post('/bots/endpoints/', { action: 'clear_error_log', bId, eId });

// ── Templates ─────────────────────────────────────────────────
export const listTemplates   = (bId)         => API.get('/bots/templates/',  { params: { bId } });
export const createTemplate  = (bId, data)   => API.post('/bots/templates/', { action: 'create', bId, ...data }, { params: { bId } });
export const deleteTemplate  = (bId, name)   => API.post('/bots/templates/', { action: 'delete', bId, name }, { params: { bId } });
export const editTemplate    = (bId, name, data) => API.post('/bots/templates/', { action: 'edit', bId, name, ...data }, { params: { bId } });
export const setTemplatePause   = (bId, name, data) => API.post('/bots/templates/', { action: 'set_pause', bId, name, ...data }, { params: { bId } });
export const clearTemplatePause = (bId, name)       => API.post('/bots/templates/', { action: 'clear_pause', bId, name }, { params: { bId } });

// ── Webhook Settings ─────────────────────────────────────────
export const getWebhookSettings    = ()   => API.get('/settings/webhook/');
export const regenerateWebhookToken = ()  => API.post('/settings/webhook/', { action: 'regenerate' });

// ── Bot Storage KV ────────────────────────────────────────────
export const listStorageChats   = (bId, limit = 50, offset = 0) =>
  API.get('/bots/storage/', { params: { bId, limit, offset } });

export const listStorageEntries = (bId, chatId) =>
  API.get('/bots/storage/', { params: { bId, chatId } });

export const clearStorageChat   = (bId, chatId) =>
  API.post('/bots/storage/', { action: 'clear_chat', bId, chatId });

export const clearStorageBot    = (bId) =>
  API.post('/bots/storage/', { action: 'clear_bot', bId });

export const setStorageKey      = (bId, chatId, key, value) =>
  API.post('/bots/storage/', { action: 'set', bId, chatId, key, value });

export const deleteStorageKey   = (bId, chatId, key) =>
  API.post('/bots/storage/', { action: 'delete_key', bId, chatId, key });

// ── Inbox ─────────────────────────────────────────────────────
export const getInboxConversations = (bId, params = {}) =>
  API.get('/bots/inbox/', { params: { bId, view: 'conversations', ...params } });

export const getInboxMessages = (bId, chatId, extraParams = {}) =>
  API.get('/bots/inbox/', { params: { bId, view: 'messages', chatId, ...extraParams } });

export const searchInboxMessages = (bId, q) =>
  API.get('/bots/inbox/', { params: { bId, view: 'search_messages', q } });

export const getInboxReports = (bId, range = 'week') =>
  API.get('/bots/inbox/', { params: { bId, view: 'reports', range } });

export const getInboxAgents = (bId) =>
  API.get('/bots/inbox/', { params: { bId, view: 'agents' } });

export const inboxHandoff  = (bId, chatId) =>
  API.post('/bots/inbox/', { action: 'handoff', bId, chatId });

export const inboxAssign   = (bId, chatId, agentId) =>
  API.post('/bots/inbox/', { action: 'assign', bId, chatId, agentId });

export const inboxUnassign = (bId, chatId) =>
  API.post('/bots/inbox/', { action: 'unassign', bId, chatId });

export const inboxReply    = (bId, chatId, agentId, text, replyToWaId = null) =>
  API.post('/bots/inbox/', { action: 'reply', bId, chatId, agentId, text, ...(replyToWaId ? { replyToWaId } : {}) });

export const inboxAddNote = (bId, chatId, agentId, text) =>
  API.post('/bots/inbox/', { action: 'add_note', bId, chatId, agentId, text });

export const inboxReact = (bId, chatId, waId, emoji) =>
  API.post('/bots/inbox/', { action: 'react', bId, chatId, waId, emoji });

export const inboxResolve  = (bId, chatId) =>
  API.post('/bots/inbox/', { action: 'resolve', bId, chatId });

export const inboxHandback = (bId, chatId) =>
  API.post('/bots/inbox/', { action: 'handback', bId, chatId });

export const inboxCreateAgent = (bId, data) =>
  API.post('/bots/inbox/', { action: 'create_agent', bId, ...data });

export const inboxUpdateAgent = (bId, data) =>
  API.post('/bots/inbox/', { action: 'update_agent', bId, ...data });

export const inboxDeleteAgent = (bId, agentId) =>
  API.post('/bots/inbox/', { action: 'delete_agent', bId, agentId });

export const inboxFetchMedia  = (bId, chatId, mId, waId) =>
  API.get('/bots/inbox/media.php', { params: { bId, chatId, mId, waId } });

export const inboxClearMessages = (bId, chatId) =>
  API.post('/bots/inbox/', { action: 'clear_messages', bId, chatId });

export const inboxReopenConversation = (bId, chatId) =>
  API.post('/bots/inbox/', { action: 'reopen', bId, chatId });

// ── Canned responses (saved replies) ────────────────────────────
export const getInboxCannedResponses = (bId) =>
  API.get('/bots/inbox/', { params: { bId, view: 'canned_responses' } });

export const inboxCreateCanned = (bId, data) =>
  API.post('/bots/inbox/', { action: 'create_canned', bId, ...data });

export const inboxUpdateCanned = (bId, data) =>
  API.post('/bots/inbox/', { action: 'update_canned', bId, ...data });

export const inboxDeleteCanned = (bId, crId) =>
  API.post('/bots/inbox/', { action: 'delete_canned', bId, crId });

// ── Conversation labels (tags) ──────────────────────────────────
export const getInboxLabels = (bId) =>
  API.get('/bots/inbox/', { params: { bId, view: 'labels' } });

export const inboxCreateLabel = (bId, data) =>
  API.post('/bots/inbox/', { action: 'create_label', bId, ...data });

export const inboxUpdateLabel = (bId, data) =>
  API.post('/bots/inbox/', { action: 'update_label', bId, ...data });

export const inboxDeleteLabel = (bId, lId) =>
  API.post('/bots/inbox/', { action: 'delete_label', bId, lId });

export const inboxSetLabels = (bId, chatId, labelIds) =>
  API.post('/bots/inbox/', { action: 'set_labels', bId, chatId, labelIds });

// ── Contact profile (Contact Profile Sidebar / CRM piece) ───────
export const getInboxContact = (bId, chatId) =>
  API.get('/bots/inbox/', { params: { bId, view: 'contact', chatId } });

export const getInboxContactHistory = (bId, chatId) =>
  API.get('/bots/inbox/', { params: { bId, view: 'contact_history', chatId } });

export const inboxSetContactField = (bId, chatId, key, value) =>
  API.post('/bots/inbox/', { action: 'set_contact_field', bId, chatId, key, value });

// ── Broadcasts ────────────────────────────────────────────────
export const listBroadcasts    = (bId) =>
  API.get('/bots/broadcasts/', { params: { bId } });

export const getBroadcast      = (bId, broadcastId, limit = 100, offset = 0) =>
  API.get('/bots/broadcasts/', { params: { bId, broadcastId, limit, offset } });

export const createBroadcast   = (data) =>
  API.post('/bots/broadcasts/', { action: 'create', ...data });

export const updateBroadcast   = (broadcastId, data) =>
  API.post('/bots/broadcasts/', { action: 'update', broadcastId, ...data });

export const sendBroadcast     = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'send', bId, broadcastId });

export const resendBroadcast   = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'resend', bId, broadcastId });

export const stopBroadcast     = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'stop', bId, broadcastId });

export const pauseBroadcast    = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'pause', bId, broadcastId });

export const resumeBroadcast   = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'resume', bId, broadcastId });

export const testSendBroadcast = (bId, broadcastId, testNumber) =>
  API.post('/bots/broadcasts/', { action: 'test_send', bId, broadcastId, testNumber });

export const previewBroadcastAudience = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'preview', bId, broadcastId });

export const deleteBroadcast   = (bId, broadcastId) =>
  API.post('/bots/broadcasts/', { action: 'delete', bId, broadcastId });

// ── Broadcast Image Upload ────────────────────────────────────
export const uploadBroadcastImage = (formData) =>
  API.post('/bots/broadcasts/upload-image/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// ── In-chat commerce: PocketFi merchant connection ───────────
export const getPocketfiMerchant = (bId) =>
  API.get('/bots/pocketfi-merchant/', { params: { bId } });

export const savePocketfiMerchant = (bId, data) =>
  API.post('/bots/pocketfi-merchant/', { bId, ...data });

export const disablePocketfiMerchant = (bId) =>
  API.post('/bots/pocketfi-merchant/', { bId, action: 'disable' });

// ── In-chat commerce: Products ────────────────────────────────
export const getProducts = (bId) =>
  API.get('/bots/products/', { params: { bId } });

export const createProduct = (bId, data) =>
  API.post('/bots/products/', { bId, action: 'create', ...data });

export const updateProduct = (bId, data) =>
  API.post('/bots/products/', { bId, action: 'update', ...data });

export const deleteProduct = (bId, prId) =>
  API.post('/bots/products/', { bId, action: 'delete', prId });

// ── Agent portal (separate session from the main subscriber login) ──
// Agents log in directly with { bId, email, password } -- no platform
// account needed. This uses its own axios instance + its own token
// key so it never collides with, or gets logged out by, the main
// subscriber session's interceptors above.
export const AGENT_API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
});

AGENT_API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('agentAuthToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

AGENT_API.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('agentAuthToken');
      localStorage.removeItem('agentInfo');
      if (window.location.pathname !== '/agent') window.location.href = '/agent';
    }
    return Promise.reject(err);
  }
);

export const agentLogin  = (bId, email, password) =>
  API.post('/agent/login/', { bId, email, password });

export const agentLogout = () =>
  AGENT_API.post('/agent/logout/', {});

export const getAgentConversations = (status = 'open') =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'conversations', status } });

export const getAgentMessages = (chatId, extraParams = {}) =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'messages', chatId, ...extraParams } });

export const searchAgentMessages = (q) =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'search_messages', q } });

export const agentReply    = (chatId, text) =>
  AGENT_API.post('/agent/inbox/', { action: 'reply', chatId, text });

export const agentAddNote = (chatId, text) =>
  AGENT_API.post('/agent/inbox/', { action: 'add_note', chatId, text });

export const getAgentLabels = () =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'labels' } });

export const agentSetLabels = (chatId, labelIds) =>
  AGENT_API.post('/agent/inbox/', { action: 'set_labels', chatId, labelIds });

export const getAgentContact = (chatId) =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'contact', chatId } });

export const getAgentContactHistory = (chatId) =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'contact_history', chatId } });

export const agentSetContactField = (chatId, key, value) =>
  AGENT_API.post('/agent/inbox/', { action: 'set_contact_field', chatId, key, value });

export const agentHandback = (chatId) =>
  AGENT_API.post('/agent/inbox/', { action: 'handback', chatId });

export const agentClearMessages = (chatId) =>
  AGENT_API.post('/agent/inbox/', { action: 'clear_messages', chatId });

export const getAgentCannedResponses = () =>
  AGENT_API.get('/agent/inbox/', { params: { view: 'canned_responses' } });
// ── Phone Number Registration (bring your own number) ──────────
export const listPhoneRegistrations = () =>
  API.get('/phone-registration/');
export const addPhoneNumber = (token, wabaId, cc, phoneNumber, displayName) =>
  API.post('/phone-registration/', { action: 'add_number', token, wabaId, cc, phoneNumber, displayName });
export const requestPhoneCode = (prId, token, codeMethod) =>
  API.post('/phone-registration/', { action: 'request_code', prId, token, codeMethod });
export const verifyPhoneCode = (prId, token, code) =>
  API.post('/phone-registration/', { action: 'verify_code', prId, token, code });
export const completePhoneRegistration = (prId, token, pin, pinConfirm) =>
  API.post('/phone-registration/', { action: 'complete', prId, token, pin, pinConfirm });

// ── Rent Space (user) ────────────────────────────────────────
export const getRentSpaceInfo = () => API.get('/rent-space/');
export const getMyRental = () => API.get('/rent-space/', { params: { action: 'my_rental' } });
export const getMyRentals = () => API.get('/rent-space/', { params: { action: 'my_rentals' } });
export const rentSpaceAddNumber = (cc, phoneNumber, displayName) =>
  API.post('/rent-space/', { action: 'add_number', cc, phoneNumber, displayName });
export const rentSpaceRequestCode = (rnId, codeMethod) =>
  API.post('/rent-space/', { action: 'request_code', rnId, codeMethod });
export const rentSpaceVerifyCode = (rnId, code) =>
  API.post('/rent-space/', { action: 'verify_code', rnId, code });
export const rentSpaceComplete = (rnId, pin, pinConfirm) =>
  API.post('/rent-space/', { action: 'complete', rnId, pin, pinConfirm });
export const rentSpaceRenew = rnId =>
  API.post('/rent-space/', { action: 'renew', rnId });
export const rentSpaceRetryPayment = rnId =>
  API.post('/rent-space/', { action: 'retry_payment', rnId });
export const createBotFromRental = (rnId, bName) =>
  API.post('/bots/', { action: 'create', bName, rnId });